import { useBooking } from '@/contexts/BookingContext';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import {
	MapPin,
	Star,
	ChevronRight,
	Ticket,
	Phone,
	ChevronDown,
	Tag,
	Landmark,
	Search,
	Copy,
} from 'lucide-react';
import {
	formatTime,
	groupWorkingHoursByDay,
	toSentenceCase,
	getCurrentOpenStatus,
} from '@/lib/utils';
import { BookingStep, Promo, Service } from '@/types/booking';
import { useState, useEffect, useRef } from 'react';
import VoucherRedemptionModal from '../VoucherRedemptionModal';
import { usePreserveQueryParams } from '@/hooks/usePreserveQueryParams';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import VoucherCard from '@/components/ui/voucher_card';
import CartSummary from '../CartSummary';
import CartDialog from '../CartDialog';
import ServiceTile from '../ServiceTile';
import {
	generateLocationKeywords,
	generateSeoDescription,
	generateSeoTitle,
} from '@/lib/seoUtils';
import ImageCarousel from '@/components/ui/ImageCarousel';
import { useImageLoader } from '@/hooks/useImageLoader';
import ServicesLoadingSpinner from '../ServicesLoadingSpinner';

export default function LandingPage() {
	useScrollToTop();
	const { navigateWithParams } = usePreserveQueryParams();
	const {
		businessInfo,
		salons,
		selectedSalon,
		formattedServices,
		formattedPackages,
		selectedServices,
		selectedPackages,
		giftCardCart,
		selectService,
		selectServiceWithVariant,
		modifyPackageInCart,
		setGiftCardCart,
		updateGiftCardCartQuantity,
		clearGiftCardCart,
		handleNextStep,
		isMobile,
		setOpenVoucherRedemptionModal,
		reviews,
		promos,
		setPromoCode,
		addToast,
		faqs,
		loadingServices,
	} = useBooking();

	const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
	const [expandedFaqs, setExpandedFaqs] = useState<string[]>([]);
	const [activeSection, setActiveSection] = useState<
		| 'services'
		| 'packages'
		| 'vouchers'
		| 'giftcards'
		| 'promos'
		| 'team'
		| 'reviews'
		| 'about'
		| 'faqs'
	>('services');
	const [servicesSearchQuery, setServicesSearchQuery] = useState('');
	const [isCartOpen, setIsCartOpen] = useState(false);
	const [isSalonDialogOpen, setIsSalonDialogOpen] = useState(false);
	const [isFirstLoad, setIsFirstLoad] = useState(() => {
		// Check if this is truly the first load in this session
		return !sessionStorage.getItem('salon-dialog-shown');
	});
	const headerRef = useRef<HTMLDivElement>(null);
	const [headerHeight, setHeaderHeight] = useState(0);
	const cartCount =
		selectedServices.length + selectedPackages.length + (giftCardCart ? 1 : 0);

	const isVoucherPurchase = selectedPackages.length > 0;
	const isGiftCardPurchase = Boolean(giftCardCart);

	const giftCardSetting = businessInfo?.giftCardSettingForBooking;
	const showGiftcards = Boolean(
		giftCardSetting?.enabled && giftCardSetting?.online !== false,
	);

	const currencySymbol = businessInfo?.country?.currency?.[0]?.symbol || '₦';
	const giftCardMinAmount = giftCardSetting?.minAmount ?? 0;
	const giftCardMaxAmount = giftCardSetting?.maxAmount ?? 0;

	const [giftCardAmountDraft, setGiftCardAmountDraft] = useState<string>('');
	const [giftCardMessageDraft, setGiftCardMessageDraft] = useState<string>('');
	const [giftCardQuantityDraft, setGiftCardQuantityDraft] = useState<number>(1);

	useEffect(() => {
		if (!giftCardCart) return;
		setGiftCardAmountDraft(String(giftCardCart.amount));
		setGiftCardMessageDraft(giftCardCart.message || '');
		setGiftCardQuantityDraft(giftCardCart.quantity || 1);
	}, [giftCardCart]);

	const handleAddGiftCardToCart = () => {
		if (!selectedSalon?.id) {
			addToast({
				message: 'Please select a location to continue.',
				variant: 'error',
			});
			return;
		}
		if (!showGiftcards) {
			addToast({
				message: 'Gift cards are not available right now.',
				variant: 'error',
			});
			return;
		}
		const amount = Number(giftCardAmountDraft);
		if (!Number.isFinite(amount) || amount <= 0) {
			addToast({ message: 'Please enter a valid amount.', variant: 'error' });
			return;
		}
		if (giftCardMinAmount && amount < giftCardMinAmount) {
			addToast({
				message: `Amount must be at least ${currencySymbol}${giftCardMinAmount.toLocaleString()}`,
				variant: 'error',
			});
			return;
		}
		if (giftCardMaxAmount && amount > giftCardMaxAmount) {
			addToast({
				message: `Amount must not exceed ${currencySymbol}${giftCardMaxAmount.toLocaleString()}`,
				variant: 'error',
			});
			return;
		}

		if (selectedServices.length > 0 || selectedPackages.length > 0) {
			addToast({
				message: 'Please clear your cart before adding a gift card.',
				variant: 'error',
			});
			return;
		}

		setGiftCardCart({
			amount,
			message: giftCardMessageDraft,
			quantity: Math.max(1, Math.floor(giftCardQuantityDraft || 1)),
		});
		addToast({ message: 'Gift card added to cart.', variant: 'success' });
	};

	const limitToWords = (value: string, maxWords: number) => {
		const words = value.trim().split(/\s+/).filter(Boolean);
		if (words.length <= maxWords) return value;
		return words.slice(0, maxWords).join(' ');
	};

	useEffect(() => {
		const vouchersForRenderDebug = formattedPackages.map((pkg) => {
			const isSelected = selectedPackages.some((p) => p.id === pkg.id);
			const priceSource = pkg?.usesCustomPrice ? 'customPrice' : 'totalPrice';
			const price = pkg?.usesCustomPrice ? pkg?.customPrice : pkg?.totalPrice;

			return {
				id: pkg.id,
				name: pkg.name,
				selected: isSelected,
				isPurchasableAsVoucher: pkg.isPurchasableAsVoucher,
				priceSource,
				price,
				usesCustomPrice: pkg.usesCustomPrice,
				customPrice: pkg.customPrice,
				totalPrice: pkg.totalPrice,
				validityMonths: pkg.validityMonths || 1,
				packageServicesCount: pkg.packageServices?.length || 0,
				packageServices: (pkg.packageServices || []).map((ps: any) => ({
					quantity: ps.quantity,
					serviceName: ps.service?.name || ps.name,
				})),
			};
		});

		console.log('Vouchers (render checks):', {
			hasVouchersSection: formattedPackages.length > 0,
			voucherCount: formattedPackages.length,
			vouchers: vouchersForRenderDebug,
		});
	}, [formattedPackages, selectedPackages]);

	// Validate single header image (carousel handles its own validation)
	const singleHeaderImageUrl =
		businessInfo?.headerImageUrls?.length === 1
			? businessInfo.headerImageUrls[0].imageUrl
			: null;
	const singleHeaderImageValid = useImageLoader(singleHeaderImageUrl);

	// Validate logo for SEO meta tags
	const logoValid = useImageLoader(businessInfo?.logoUrl);

	// Filter services based on search query
	const filteredServices = formattedServices
		.map((category) => ({
			...category,
			services: category.services.filter((service) => {
				if (!servicesSearchQuery.trim()) return true;
				const searchTerm = servicesSearchQuery.toLowerCase();
				return (
					service.name.toLowerCase().includes(searchTerm) ||
					category.name.toLowerCase().includes(searchTerm)
				);
			}),
		}))
		.filter((category) => category.services.length > 0); // Only show categories with matching services

	// SEO data
	const allServices: Service[] = formattedServices.flatMap(
		(group) => group.services,
	);

	const seoTitle =
		businessInfo?.name && allServices.length > 0
			? generateSeoTitle(
					businessInfo.name,
					allServices,
					selectedSalon,
					businessInfo?.businessSpecialties,
				)
			: businessInfo?.name
				? `Book an appointment at ${businessInfo.name}`
				: 'Book an appointment';

	// Also set document title directly as a fallback
	useEffect(() => {
		if (seoTitle && seoTitle !== 'Book an appointment') {
			document.title = seoTitle;
		}
	}, [seoTitle]);

	const seoDescription = businessInfo?.name
		? generateSeoDescription(
				businessInfo.name,
				businessInfo.about,
				formattedServices,
				selectedSalon,
			)
		: 'Book your appointment online. Easy online booking for professional services.';

	const seoKeywords =
		formattedServices.length > 0
			? generateLocationKeywords(formattedServices, selectedSalon).join(', ')
			: '';

	const canonicalUrl =
		typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';

	const structuredData = businessInfo
		? {
				'@context': 'https://schema.org',
				'@type': 'LocalBusiness',
				name: businessInfo.name,
				description: businessInfo.about,
				url: canonicalUrl,
				...(businessInfo.logoUrl && { logo: businessInfo.logoUrl }),
				...(selectedSalon && {
					address: {
						'@type': 'PostalAddress',
						streetAddress: selectedSalon.address,
						addressLocality: selectedSalon.city,
						addressRegion: selectedSalon.state,
					},
				}),
				...(selectedSalon?.phone && {
					telephone: `${selectedSalon.callingCode}${selectedSalon.phone}`,
				}),
				hasOfferCatalog: {
					'@type': 'OfferCatalog',
					name: 'Services',
					itemListElement: formattedServices.slice(0, 10).map((service) => ({
						'@type': 'Offer',
						itemOffered: {
							'@type': 'Service',
							name: service.name,
							description: '',
						},
					})),
				},
			}
		: null;

	// Auto-open salon selection dialog on first page launch
	useEffect(() => {
		// Only open if there are multiple salons and it's the first load
		if (salons && salons.length > 1 && isFirstLoad) {
			setIsSalonDialogOpen(true);
			setIsFirstLoad(false);
		}
	}, [salons, isFirstLoad]);

	// Auto-expand categories when searching
	useEffect(() => {
		if (servicesSearchQuery.trim()) {
			const categoriesWithResults = filteredServices.map((cat) => cat.name);
			setExpandedCategories(categoriesWithResults);
		}
	}, [servicesSearchQuery, filteredServices]);

	// Auto-expand categories that contain selected services
	useEffect(() => {
		if (formattedServices.length > 0 && selectedServices.length > 0) {
			const categoriesToExpand = formattedServices
				.filter((category) =>
					category.services.some((service) =>
						selectedServices.some((selected) => selected.id === service.id),
					),
				)
				.map((category) => category.name);

			if (categoriesToExpand.length > 0) {
				setExpandedCategories((prev) => {
					const newExpanded = [...new Set([...prev, ...categoriesToExpand])];
					return newExpanded;
				});
			}
		}
	}, [formattedServices, selectedServices]);

	// Calculate header height for sticky positioning
	useEffect(() => {
		const updateHeaderHeight = () => {
			if (headerRef.current) {
				setHeaderHeight(headerRef.current.offsetHeight);
			}
		};

		updateHeaderHeight();
		window.addEventListener('resize', updateHeaderHeight);
		return () => window.removeEventListener('resize', updateHeaderHeight);
	}, [businessInfo, reviews, selectedSalon]);

	// Smooth scroll to section functionality
	const scrollToSection = (sectionId: string) => {
		const element = document.getElementById(sectionId);
		if (element && headerRef.current) {
			const headerHeight = headerRef.current.offsetHeight;
			const elementPosition = element.offsetTop - headerHeight - 40; // Increased offset to ensure clear visibility

			window.scrollTo({
				top: elementPosition,
				behavior: 'smooth',
			});
		}
	};

	// Scroll spy functionality to detect active section
	useEffect(() => {
		const handleScroll = () => {
			if (!headerRef.current) return;

			const headerHeight = headerRef.current.offsetHeight;
			const scrollPosition = window.scrollY + headerHeight + 100; // 100px threshold for better UX

			const sections = [
				'services',
				'vouchers',
				...(showGiftcards ? ['giftcards'] : []),
				'promos',
				'reviews',
				'about',
				...(faqs && faqs.length > 0 ? ['faqs'] : []),
			];
			let activeSection = 'services'; // Default to services

			// Find the section that's currently most in view
			for (const sectionId of sections) {
				const element = document.getElementById(sectionId);
				if (element) {
					const elementTop = element.offsetTop;
					const elementBottom = elementTop + element.offsetHeight;

					// Check if the section is in view with threshold
					if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
						activeSection = sectionId;
						break;
					}

					// If we're past all sections, set the last one as active
					if (scrollPosition >= elementTop) {
						activeSection = sectionId;
					}
				}
			}

			setActiveSection(activeSection as any);
		};

		// Throttle scroll events for better performance
		let timeoutId: NodeJS.Timeout;
		const throttledHandleScroll = () => {
			if (timeoutId) clearTimeout(timeoutId);
			timeoutId = setTimeout(handleScroll, 50); // 50ms throttle
		};

		window.addEventListener('scroll', throttledHandleScroll);
		handleScroll(); // Check initial position

		return () => {
			window.removeEventListener('scroll', throttledHandleScroll);
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [headerHeight, faqs]);

	const toggleCategory = (categoryName: string) => {
		setExpandedCategories((prev) =>
			prev.includes(categoryName)
				? prev.filter((c) => c !== categoryName)
				: [...prev, categoryName],
		);
	};

	const toggleFaq = (faqId: string) => {
		setExpandedFaqs((prev) =>
			prev.includes(faqId)
				? prev.filter((id) => id !== faqId)
				: [...prev, faqId],
		);
	};

	return (
		<>
			<Helmet>
				<title>{seoTitle}</title>
				<meta name="description" content={seoDescription} />
				{seoKeywords && <meta name="keywords" content={seoKeywords} />}
				<link rel="canonical" href={canonicalUrl} />

				{/* Open Graph tags for social sharing */}
				<meta property="og:title" content={seoTitle} />
				<meta property="og:description" content={seoDescription} />
				<meta property="og:type" content="website" />
				<meta property="og:url" content={canonicalUrl} />
				{logoValid && businessInfo?.logoUrl && (
					<meta property="og:image" content={businessInfo.logoUrl} />
				)}

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={seoTitle} />
				<meta name="twitter:description" content={seoDescription} />
				{logoValid && businessInfo?.logoUrl && (
					<meta name="twitter:image" content={businessInfo.logoUrl} />
				)}

				{structuredData && (
					<script type="application/ld+json">
						{JSON.stringify(structuredData)}
					</script>
				)}
			</Helmet>

			<div className="bg-grey-10 overflow-x-hidden max-w-6xl mx-auto py-8 pb-4">
				{businessInfo?.headerImageUrls &&
					businessInfo.headerImageUrls.length > 0 && (
						<div className="relative">
							{businessInfo.headerImageUrls.length === 1 ? (
								// Single image - only show if it loads successfully
								singleHeaderImageValid && (
									<div className="relative h-64 md:h-80 lg:h-96 w-full overflow-hidden">
										<img
											src={businessInfo.headerImageUrls[0].imageUrl}
											alt={businessInfo.name}
											className="w-full h-full object-cover"
										/>
										{/* Overlay with business name on mobile */}
										<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent lg:hidden">
											<div className="absolute bottom-0 left-0 right-0 p-4">
												<h1 className="text-2xl font-bold text-white">
													{businessInfo?.name}
												</h1>
												<p className="text-white/90 text-sm mt-1">
													{selectedSalon?.address}
													{selectedSalon?.city && `, ${selectedSalon.city}`}
												</p>
											</div>
										</div>
									</div>
								)
							) : (
								// Multiple images - ImageCarousel handles its own validation
								<ImageCarousel
									images={businessInfo.headerImageUrls}
									businessName={businessInfo.name}
									selectedSalon={selectedSalon || undefined}
								/>
							)}
						</div>
					)}

				{/* Compact Header - Made Sticky */}
				<div
					ref={headerRef}
					className="sticky top-0 bg-white z-40 border-b border-grey-75"
				>
					<div className="max-w-6xl mx-auto px-4 py-6 pb-0">
						{/* Business Header */}
						<div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
							<div className="flex-1">
								<h1
									className={`text-3xl lg:text-4xl font-semibold text-grey-900 mb-3 tracking-tight font-heading ${
										(businessInfo?.headerImageUrls?.length === 1 &&
											singleHeaderImageValid) ||
										(businessInfo?.headerImageUrls &&
											businessInfo.headerImageUrls.length > 1)
											? 'hidden lg:block'
											: ''
									}`}
								>
									{businessInfo?.name}
								</h1>

								<div className="flex flex-wrap items-center gap-4 text-sm">
									{/* Rating */}
									{reviews &&
										reviews.length > 0 &&
										businessInfo?.giftCardSettingForBooking?.online && (
											<div className="flex items-center gap-1.5">
												<Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
												<span className="font-semibold text-grey-900">
													{(
														reviews.reduce(
															(acc: number, r: any) => acc + (r.stars || 0),
															0,
														) / reviews.length
													).toFixed(1)}
												</span>
												<span className="text-grey-500 whitespace-nowrap">
													({reviews.length} review
													{reviews.length !== 1 ? 's' : ''})
												</span>
											</div>
										)}

									{/* Location with Salon Switcher */}
									{salons && salons.length > 1 ? (
										<>
											<button
												onClick={() => setIsSalonDialogOpen(true)}
												className="flex items-center gap-1.5 bg-grey-50 px-2 py-1 rounded-lg transition-colors hover:bg-grey-100 max-w-full"
											>
												<MapPin className="h-4 w-4 text-grey-500 shrink-0" />
												<span className="text-grey-700 truncate flex-1 min-w-0">
													{selectedSalon?.branchName ||
														selectedSalon?.address ||
														'Select a salon'}
													{selectedSalon?.city && `, ${selectedSalon.city}`}
												</span>
												<ChevronDown className="h-3.5 w-3.5 text-grey-500 ml-1 shrink-0" />
											</button>

											<Dialog
												open={isSalonDialogOpen}
												onOpenChange={setIsSalonDialogOpen}
											>
												<DialogContent className="max-w-md w-full bg-white">
													<DialogHeader>
														<DialogTitle className="text-lg font-semibold text-grey-900">
															Select a location
														</DialogTitle>
													</DialogHeader>
													<div className="flex flex-col gap-2 max-h-96 overflow-y-auto w-full">
														{salons.map((salon) => (
															<button
																key={salon.id}
																className={`w-full text-left px-4 py-3 rounded-lg border transition-colors hover:bg-grey-50 ${
																	selectedSalon?.id === salon.id
																		? 'bg-green-300/10 border-green-300'
																		: 'border-grey-100'
																}`}
																style={{ minWidth: 0 }}
																onClick={() => {
																	// Navigate to the new salon URL
																	const businessUrl =
																		businessInfo?.bookingUrlIdentifier ||
																		businessInfo?.urlIdentifier;
																	const salonUrl =
																		salon.urlIdentifier || salon.id;
																	setIsSalonDialogOpen(false);
																	setIsFirstLoad(false);
																	// Mark that the dialog has been shown and salon selected
																	sessionStorage.setItem(
																		'salon-dialog-shown',
																		'true',
																	);
																	navigateWithParams(
																		`/${businessUrl}/${salonUrl}`,
																	);
																}}
															>
																<div className="flex items-start gap-2 w-full min-w-0">
																	<Landmark className="h-5 w-5 text-green-300 flex-shrink-0" />
																	<div className="flex-1 min-w-0 overflow-hidden">
																		<div className="font-medium text-grey-900 truncate">
																			{salon.branchName || salon.address}
																		</div>
																		<div className="text-sm text-grey-600 truncate">
																			{salon.address}
																			{salon.city && `, ${salon.city}`}
																		</div>
																		{(() => {
																			// Vouchers are business-wide: count unique voucher packages across ALL salons
																			const allVouchers = salons
																				.flatMap((s) => s.packages || [])
																				.filter(
																					(pkg, index, self) =>
																						pkg.isPurchasableAsVoucher ===
																							true &&
																						self.findIndex(
																							(p) => p.id === pkg.id,
																						) === index,
																				);
																			const voucherCount = allVouchers.length;
																			return voucherCount > 0 ? (
																				<div className="text-xs text-green-300 mt-1 truncate">
																					{voucherCount} voucher
																					{voucherCount > 1 ? 's' : ''}{' '}
																					available
																				</div>
																			) : null;
																		})()}
																	</div>
																</div>
															</button>
														))}
													</div>
												</DialogContent>
											</Dialog>
										</>
									) : (
										<div className="flex items-center gap-1.5 max-w-full">
											<MapPin className="h-4 w-4 text-grey-500 shrink-0" />
											<span className="text-grey-700 truncate flex-1 min-w-0">
												{selectedSalon?.address}
												{selectedSalon?.city && `, ${selectedSalon.city}`}
											</span>
										</div>
									)}
								</div>
							</div>

							{promos &&
								promos.length > 0 &&
								businessInfo?.showPromosOnBookingSite && (
									<div className="flex items-center gap-3">
										{promos.map((promo, index) => (
											<div
												key={index}
												className="flex items-center gap-2 bg-green-300/10 px-3 py-1.5 rounded-md"
											>
												<Tag className="h-4 w-4 text-green-300/50" />
												<span className="text-green-300 text-sm font-medium">
													{promo.title}
												</span>
												<button
													onClick={() => {
														navigator.clipboard.writeText(promo.title);
														addToast({
															message: 'Copied to clipboard',
															variant: 'success',
														});
													}}
													className="ml-1 p-1 hover:bg-green-100 rounded-md transition-colors"
												>
													<Copy className="h-3.5 w-3.5 text-green-300/50" />
												</button>
											</div>
										))}
									</div>
								)}

							<div className="flex gap-3">
								<Button
									variant="outline"
									size="sm"
									onClick={() => setOpenVoucherRedemptionModal(true)}
									className="flex items-center gap-2"
								>
									<Ticket className="h-4 w-4" />
									<span>Redeem voucher</span>
								</Button>
							</div>
						</div>
						{/* Status */}
						{/* Status */}
						<div className="flex items-center gap-4 mt-4">
							<div className="flex items-center gap-1.5">
								{(() => {
									const { isOpen, todayHours } = getCurrentOpenStatus(
										selectedSalon?.salonHours || [],
										(businessInfo as any)?.timezone,
										selectedSalon?.closedPeriods || [],
									);

									if (isOpen && todayHours && todayHours.length > 0) {
										return (
											<>
												<div className="w-2 h-2 bg-green-500 rounded-full"></div>
												<span className="text-grey-700 font-medium">
													Open -{' '}
													{todayHours
														.sort((a, b) => {
															const aTime = a.openTime.split(':').map(Number);
															const bTime = b.openTime.split(':').map(Number);
															const aMinutes = aTime[0] * 60 + aTime[1];
															const bMinutes = bTime[0] * 60 + bTime[1];
															return aMinutes - bMinutes;
														})
														.map((slot, index) => (
															<span key={index}>
																{formatTime(slot.openTime)} to{' '}
																{formatTime(slot.closeTime)}
																{index < todayHours.length - 1 ? ', ' : ''}
															</span>
														))}
												</span>
											</>
										);
									} else {
										const { nextOpenTime } = getCurrentOpenStatus(
											selectedSalon?.salonHours || [],
											(businessInfo as any)?.timezone,
											selectedSalon?.closedPeriods || [],
										);
										// Get current day in business timezone
										const now = new Date();
										let businessTime: Date;

										if ((businessInfo as any)?.timezone) {
											businessTime = new Date(
												now.toLocaleString('en-US', {
													timeZone: (businessInfo as any).timezone,
												}),
											);
										} else {
											businessTime = now;
										}

										const currentDay = businessTime
											.toLocaleDateString('en-US', { weekday: 'long' })
											.toLowerCase();

										// Check if next open time is within 7 days
										const getNextOpenDisplay = () => {
											if (!nextOpenTime) return null;

											const nextOpenDate = nextOpenTime.date
												? new Date(nextOpenTime.date)
												: null;
											const daysDifference = nextOpenDate
												? Math.ceil(
														(nextOpenDate.getTime() - now.getTime()) /
															(1000 * 60 * 60 * 24),
													)
												: 0;

											// Check if it's actually today (same day name AND within 1 day)
											const isToday =
												nextOpenTime.day === currentDay && daysDifference <= 1;

											if (isToday) {
												return formatTime(nextOpenTime.time);
											} else if (daysDifference <= 7) {
												// Within 7 days - show day name
												const dayName =
													nextOpenTime.day.charAt(0).toUpperCase() +
													nextOpenTime.day.slice(1);
												return `${formatTime(nextOpenTime.time)} (${dayName})`;
											} else {
												// Beyond 7 days - show date
												const dateStr = nextOpenDate?.toLocaleDateString(
													'en-US',
													{
														month: 'short',
														day: 'numeric',
													},
												);
												return `${formatTime(nextOpenTime.time)} (${dateStr})`;
											}
										};

										return (
											<>
												<div className="w-2 h-2 bg-red-500 shrink-0 rounded-full"></div>
												<p className="text-grey-700 font-medium">
													{nextOpenTime ? (
														<>Closed — Opens at {getNextOpenDisplay()}</>
													) : (
														'Closed'
													)}
												</p>
											</>
										);
									}
								})()}
							</div>
						</div>
						{/* Navigation Tabs - Made Sticky */}
						<div className="z-30">
							<div className="max-w-6xl mx-auto py-8 pb-4">
								<div
									hidden={businessInfo?.acceptsOnlineBookings}
									className="p-3 bg-grey-50 rounded-lg mb-4"
								>
									<p className="text-sm text-grey-600">
										This business does not accept online bookings. You can
										contact them on{' '}
										{selectedSalon?.phone && (
											<a
												href={`tel:${selectedSalon.callingCode}${selectedSalon.phone}`}
												className="underline"
											>
												{selectedSalon.callingCode}
												{selectedSalon.phone}
											</a>
										)}
									</p>
								</div>
								<div className="flex gap-2 overflow-x-scroll scrollbar-hide">
									<NavigationTab
										activeSection={activeSection}
										label="Services"
										onClick={() => {
											setActiveSection('services');
											scrollToSection('services');
										}}
									/>
									{formattedPackages.length > 0 && (
										<NavigationTab
											activeSection={activeSection}
											label="Vouchers"
											onClick={() => {
												setActiveSection('vouchers');
												setServicesSearchQuery('');
												scrollToSection('vouchers');
											}}
										/>
									)}

									{showGiftcards && (
										<NavigationTab
											activeSection={activeSection}
											label="Giftcards"
											onClick={() => {
												setActiveSection('giftcards');
												setServicesSearchQuery('');
												scrollToSection('giftcards');
											}}
										/>
									)}

									{promos &&
										promos.length > 0 &&
										businessInfo?.showPromosOnBookingSite && (
											<NavigationTab
												activeSection={activeSection}
												label="Promos"
												onClick={() => {
													setActiveSection('promos');
													setServicesSearchQuery('');
													scrollToSection('promos');
												}}
											/>
										)}

									{reviews &&
										reviews.length > 0 &&
										businessInfo?.giftCardSettingForBooking?.online && (
											<NavigationTab
												activeSection={activeSection}
												label="Reviews"
												onClick={() => {
													setActiveSection('reviews');
													setServicesSearchQuery('');
													scrollToSection('reviews');
												}}
											/>
										)}

									<NavigationTab
										activeSection={activeSection}
										label="About"
										onClick={() => {
											setActiveSection('about');
											setServicesSearchQuery('');
											scrollToSection('about');
										}}
									/>
									{faqs && faqs.length > 0 && (
										<NavigationTab
											activeSection={activeSection}
											label="FAQs"
											onClick={() => {
												setActiveSection('faqs');
												setServicesSearchQuery('');
												scrollToSection('faqs');
											}}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Main Content */}
				<div className="max-w-6xl mx-auto px-4 py-6 lg:pb-[500px]">
					<div className="flex flex-col lg:flex-row gap-6">
						<div className="flex-1">
							<section
								id="services"
								className="space-y-4"
								style={{ scrollMarginTop: `${headerHeight + 40}px` }}
							>
								{/* Show loading spinner while services are being fetched */}
								{loadingServices ? (
									<ServicesLoadingSpinner />
								) : (
									<>
										{/* Search Input for Services */}
										<div className="">
											<div className="relative">
												<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
													<Search className="h-5 w-5 text-grey-400" />
												</div>
												<input
													type="text"
													placeholder="Search services or categories..."
													value={servicesSearchQuery}
													onChange={(e) =>
														setServicesSearchQuery(e.target.value)
													}
													className="block w-full pl-10 pr-3 py-3 border placeholder:text-sm border-grey-75 rounded-lg focus:ring-2 focus:ring-green-300 focus:border-green-300 outline-none transition-colors bg-white text-grey-900 placeholder-grey-500"
												/>
											</div>
										</div>

										{/* Featured Services */}
										{!servicesSearchQuery &&
											formattedServices.some((cat) =>
												cat.services.some((s) => s.featured),
											) && (
												<>
													<div className="rounded-lg p-4">
														<h2 className="text-lg font-semibold text-grey-900 font-heading">
															Popular services
														</h2>
														<p className="text-sm text-grey-600 mt-1">
															Our most booked treatments
														</p>
													</div>

													{formattedServices
														.flatMap((cat) =>
															cat.services
																.filter((s) => s.featured)
																.slice(0, 3),
														)
														.map((service) => (
															<ServiceTile
																key={service.id}
																service={service}
																businessInfo={businessInfo}
																selected={selectedServices.some(
																	(s) => s.id === service.id,
																)}
																onClick={() => selectService(service.id)}
																onSelectVariant={selectServiceWithVariant}
																allowMultiple={true}
																selectedServices={selectedServices}
															/>
														))}
												</>
											)}

										{/* All Services by Category */}
										{filteredServices.length > 0 ? (
											filteredServices.map((category) => (
												<div
													key={category.category}
													className="bg-white rounded-lg border-1 transition-all duration-300 border-grey-75"
												>
													<div
														className={`p-4 ${
															expandedCategories.includes(category.name)
																? 'border-b border-grey-100'
																: ''
														}`}
													>
														<button
															onClick={() => toggleCategory(category.name)}
															className="cursor-pointer  w-full flex items-center justify-between group"
														>
															<div className="text-left">
																<h2 className="text-base font-semibold text-grey-900 font-heading capitalize">
																	{category.name}
																</h2>
																<p className="text-sm text-grey-600">
																	{category.services.length} service
																	{category.services.length !== 1 ? 's' : ''}
																</p>
															</div>
															<ChevronRight
																className={`h-5 w-5 text-grey-400 transition-transform ${
																	expandedCategories.includes(category.name)
																		? 'rotate-90'
																		: ''
																}`}
															/>
														</button>
													</div>

													{expandedCategories.includes(category.name) && (
														<div className="divide-y divide-grey-100">
															{category.services.map((service) => (
																<ServiceTile
																	key={service.id}
																	service={service}
																	businessInfo={businessInfo}
																	selected={selectedServices.some(
																		(s) => s.id === service.id,
																	)}
																	onClick={() => selectService(service.id)}
																	onSelectVariant={selectServiceWithVariant}
																	allowMultiple={true}
																	selectedServices={selectedServices}
																/>
															))}
														</div>
													)}
												</div>
											))
										) : (
											<div className="bg-white rounded-lg p-8 text-center">
												<Search className="h-12 w-12 text-grey-300 mx-auto mb-3" />
												<p className="text-grey-600">
													No services found matching "{servicesSearchQuery}"
												</p>
												<p className="text-sm text-grey-500 mt-1">
													Try a different search term or browse our categories
												</p>
											</div>
										)}
									</>
								)}
							</section>

							<section
								className="space-y-4"
								id="vouchers"
								style={{ scrollMarginTop: `${headerHeight + 40}px` }}
							>
								{formattedPackages.length > 0 && (
									<>
										<div className=" mb-6 mt-10">
											<h2 className="text-base font-semibold text-grey-900 mb-1 font-heading">
												Gift Vouchers
											</h2>
											<p className="text-sm text-grey-500">
												Purchase vouchers as gifts or for future use. Valid for
												the specified duration from purchase date.
											</p>
										</div>

										<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
											{formattedPackages.map((pkg) => (
												<VoucherCard
													key={pkg.id}
													selected={selectedPackages.some(
														(p) => p.id === pkg.id,
													)}
													onPurchase={() => {
														const isSelected = selectedPackages.some(
															(p) => p.id === pkg.id,
														);
														if (isSelected) {
															modifyPackageInCart(pkg.id, 'remove');
														} else {
															modifyPackageInCart(pkg.id, 'add');
														}
													}}
													pkg={pkg}
													businessInfo={businessInfo}
												/>
											))}
										</div>
									</>
								)}
							</section>

							<section
								className="space-y-4 bg-gradient-to-br from-green-300/20 via-white to-grey-50 p-4 rounded-lg border border-grey-100 mt-2"
								id="giftcards"
								style={{ scrollMarginTop: `${headerHeight + 40}px` }}
							>
								{showGiftcards && (
									<>
										<div className=" mb-6 mt-10">
											<h2 className="text-base font-semibold text-grey-900 mb-1 font-heading">
												{giftCardSetting?.name || 'Giftcards'}
											</h2>
											<p className="text-sm text-grey-500">
												{giftCardSetting?.description ||
													'Gift cards do not expire. Purchased value is added to recipient wallet and can be used anytime.'}
											</p>
										</div>

										<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
											<div className="space-y-6">
												<h3 className="text-lg font-semibold text-grey-900">
													Customize giftcard
												</h3>

												<div className="space-y-2">
													<label className="text-sm font-medium text-grey-700">
														Amount
													</label>
													<div className="flex items-center gap-3">
														<div className="px-3 py-3 border border-grey-200 rounded-lg bg-grey-50 text-grey-700 font-medium">
															{currencySymbol}
														</div>
														<input
															type="number"
															min={giftCardMinAmount || undefined}
															max={giftCardMaxAmount || undefined}
															step="0.01"
															value={giftCardAmountDraft}
															onChange={(e) =>
																setGiftCardAmountDraft(e.target.value)
															}
															placeholder={`${currencySymbol} ${giftCardMinAmount.toLocaleString()} - ${currencySymbol} ${giftCardMaxAmount.toLocaleString()}`}
															className="flex-1 border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300"
															required
														/>
													</div>
												</div>

												<div className="space-y-2">
													<label className="text-sm font-medium text-grey-700">
														Add message
													</label>
													<textarea
														value={giftCardMessageDraft}
														onChange={(e) =>
															setGiftCardMessageDraft(
																limitToWords(e.target.value, 15),
															)
														}
														placeholder="Add a message"
														rows={3}
														className="w-full border border-grey-200 rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-green-300 resize-none"
													/>
												</div>

												<div className="flex flex-col sm:flex-row gap-3 sm:items-center">
													{giftCardAmountDraft && (
														<Button
															type="button"
															variant="primary"
															className="sm:flex-1"
															onClick={handleAddGiftCardToCart}
														>
															Add to cart
														</Button>
													)}
													{/* 
	                          <div className="flex items-center justify-between sm:justify-start gap-3">
	                            <button
	                              type="button"
	                              onClick={() =>
	                                setGiftCardQuantityDraft((q) => Math.max(1, q - 1))
	                              }
	                              className="w-10 h-10 rounded-full border border-grey-200 flex items-center justify-center hover:bg-grey-50"
	                            >
	                              –
	                            </button>
	                            <span className="min-w-8 text-center font-medium text-grey-900">
	                              {giftCardQuantityDraft}
	                            </span>
	                            <button
	                              type="button"
	                              onClick={() => setGiftCardQuantityDraft((q) => q + 1)}
	                              className="w-10 h-10 rounded-full border border-grey-200 flex items-center justify-center hover:bg-grey-50"
	                            >
	                              +
	                            </button>
	                          </div> */}
												</div>

												{/* {giftCardCart && (
	                          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-100 rounded-lg">
	                            <p className="text-sm text-green-800">
	                              Gift card added to cart.
	                            </p>
	                            <button
	                              type="button"
	                              onClick={() => {
	                                clearGiftCardCart();
	                                setGiftCardAmountDraft("");
	                                setGiftCardMessageDraft("");
	                                setGiftCardQuantityDraft(1);
	                              }}
	                              className="text-sm font-medium text-green-700 hover:text-green-800"
	                            >
	                              Remove
	                            </button>
	                          </div>
	                        )} */}
											</div>

											<div className="w-full">
												<div className="w-full max-w-xl bg-[#0B6B5D] rounded-xl p-8 text-white shadow-sm">
													<div className="flex items-center justify-between">
														<span className="text-xs tracking-widest opacity-80">
															GIFT CARD
														</span>
														<span className="text-xs opacity-80">
															{businessInfo?.name || ''}
														</span>
													</div>

													<div className="mt-8">
														<p className="text-3xl font-semibold">
															{currencySymbol}
															{(
																Number(giftCardAmountDraft || 0) || 0
															).toLocaleString()}
														</p>
													</div>

													<div className="mt-10">
														<p className="text-[10px] tracking-widest opacity-70">
															MESSAGE
														</p>
														<p className="mt-2 text-sm opacity-90 line-clamp-2">
															{giftCardMessageDraft?.trim()
																? giftCardMessageDraft
																: 'Enter up to 15 words'}
														</p>
													</div>
												</div>
											</div>
										</div>
									</>
								)}
							</section>

							<section
								className="space-y-4 mb-10"
								id="promos"
								style={{ scrollMarginTop: `${headerHeight + 40}px` }}
							>
								{promos && promos.length > 0 && (
									<>
										<div className=" mt-10 mb-4">
											<h2 className="text-lg font-semibold text-grey-900 mb-2 font-heading">
												Special Offers
											</h2>
											<p className="text-sm text-grey-600">
												Limited-time promotions and discounts on our services
											</p>
										</div>

										{promos.map((promo: any) => (
											<div
												key={promo.id}
												onClick={() => setPromoCode(promo.code)}
												className="bg-white rounded-lg p-6 border-2 border-green-100 cursor-pointer"
											>
												<div className="flex items-start justify-between">
													<div className="flex-1">
														<div className="flex items-center gap-2 mb-2">
															<Tag className="h-5 w-5 text-green-300" />
															<h3 className="text-xl font-semibold text-grey-900 font-heading">
																{promo.title || promo.name}
															</h3>
														</div>
														<p className="text-grey-600 leading-relaxed mb-3">
															{promo.description}
														</p>

														{/* Validity Period */}
														{(promo.start_date || promo.end_date) && (
															<div className="mt-3 text-sm text-grey-500">
																{promo.end_date && (
																	<p>
																		Valid until{' '}
																		{new Date(
																			promo.end_date,
																		).toLocaleDateString('en-US', {
																			month: 'long',
																			day: 'numeric',
																			year: 'numeric',
																		})}
																	</p>
																)}
															</div>
														)}

														{/* Promo Code if available */}
														{promo.code && (
															<div className="mt-4 p-3 bg-grey-50 rounded-lg">
																<p className="text-xs text-grey-600 mb-1">
																	Use promo code:
																</p>
																<p className="text-lg font-mono font-bold text-grey-900">
																	{promo.code}
																</p>
															</div>
														)}
													</div>
												</div>

												{/* Terms and Conditions if available */}
												{promo.terms && (
													<div className="mt-4 pt-4 border-t border-grey-100">
														<p className="text-xs text-grey-500">
															<span className="font-medium">
																Terms & Conditions:
															</span>{' '}
															{promo.terms}
														</p>
													</div>
												)}
											</div>
										))}
									</>
								)}
							</section>

							<section
								id="reviews"
								className="space-y-4"
								style={{ scrollMarginTop: `${headerHeight + 40}px` }}
							>
								{reviews &&
									reviews.length > 0 &&
									businessInfo?.giftCardSettingForBooking?.online && (
										<div className="bg-white rounded-lg p-6">
											<div className="flex items-center gap-4 mb-6">
												<div>
													<div className="flex items-center gap-2">
														<Star className="h-6 w-6 fill-yellow-400 text-yellow-400" />
														<span className="text-3xl font-semibold tracking-tight">
															{(
																reviews.reduce(
																	(acc: number, r: any) => acc + (r.stars || 0),
																	0,
																) / reviews.length
															).toFixed(1)}
														</span>
													</div>
													<p className="text-grey-600 text-sm">
														Based on {reviews.length} review
														{reviews.length !== 1 ? 's' : ''}
													</p>
												</div>
											</div>

											<div className="space-y-4">
												{reviews.map((review: any) => (
													<div
														key={review.id}
														className="border-b border-grey-100 pb-4"
													>
														<div className="flex items-center gap-2 mb-2">
															<div className="flex">
																{[1, 2, 3, 4, 5].map((i) => (
																	<Star
																		key={i}
																		className={`h-4 w-4 ${
																			i <= review.stars
																				? 'fill-yellow-400 text-yellow-400'
																				: 'text-grey-300'
																		}`}
																	/>
																))}
															</div>
															<span className="font-semibold text-grey-900">
																{review.client?.name || 'Anonymous'}
															</span>
															<span className="text-grey-500 text-sm">
																•{' '}
																{new Date(
																	review.created_at,
																).toLocaleDateString()}
															</span>
														</div>
														<p className="text-grey-700 leading-relaxed">
															{review.message}
														</p>
													</div>
												))}
											</div>
										</div>
									)}
							</section>

							{faqs && faqs.length > 0 && (
								<section
									id="faqs"
									className="space-y-4 mt-10 mx-auto"
									style={{ scrollMarginTop: `${headerHeight + 40}px` }}
								>
									<div className="">
										<h2 className="text-xl font-semibold text-grey-900 mb-2 font-heading text-center">
											Frequently Asked Questions (FAQs)
										</h2>
										<p className="text-grey-600 text-sm mb-6 text-center">
											Get meticulously curated responses to frequently asked
											questions.
										</p>
										<div className="space-y-0 bg-white rounded-lg p-6 shadow-[3px_3px_3px_0px_rgba(0,0,0,0.02)]">
											{faqs.map((faq: any, index: number) => {
												const isExpanded = expandedFaqs.includes(
													faq.id || index.toString(),
												);
												return (
													<div
														key={faq.id || index}
														className={`border-b border-grey-100 last:border-b-0 overflow-hidden transition-all duration-300 ${
															isExpanded ? 'bg-grey-5' : ''
														}`}
													>
														<button
															onClick={() =>
																toggleFaq(faq.id || index.toString())
															}
															className="w-full py-4 px-2 flex items-start justify-between gap-4 hover:bg-grey-10 transition-colors"
														>
															<h3 className="text-base font-semibold text-grey-900 text-left flex-1">
																{faq.question}
															</h3>
															<div className="flex-shrink-0 w-6 flex items-center justify-center">
																{isExpanded ? (
																	<span className="text-grey-600 text-2xl font-light">
																		−
																	</span>
																) : (
																	<span className="text-grey-600 text-2xl font-light">
																		+
																	</span>
																)}
															</div>
														</button>
														<div
															className={`transition-all duration-500 ease-in-out overflow-hidden ${
																isExpanded
																	? 'max-h-[1000px] opacity-100'
																	: 'max-h-0 opacity-0'
															}`}
														>
															<div className="px-2 pb-4 pt-0 text-grey-700 leading-relaxed animate-in slide-in-from-top-2 duration-300">
																{faq.answer}
															</div>
														</div>
													</div>
												);
											})}
										</div>
									</div>
								</section>
							)}

							<section
								id="about"
								className="space-y-4 mt-10"
								style={{ scrollMarginTop: `${headerHeight + 40}px` }}
							>
								{businessInfo?.about && (
									<div className="bg-white rounded-lg p-6">
										<h2 className="text-xl font-semibold text-grey-900 mb-3 font-heading">
											About
										</h2>
										<p className="text-grey-700 leading-relaxed">
											{businessInfo.about}
										</p>
									</div>
								)}

								{/* Map Section */}
								<div className="bg-white rounded-lg p-6">
									<div className="relative rounded-lg overflow-hidden h-64">
										<iframe
											width="100%"
											height="100%"
											frameBorder="0"
											style={{ border: 0 }}
											src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
												`${selectedSalon?.address || ''} ${
													selectedSalon?.city || ''
												} ${selectedSalon?.state || ''}`,
											)}&zoom=15`}
											allowFullScreen
										/>
									</div>
									<div className="mt-3 flex items-center justify-between">
										<div className="text-sm text-grey-600">
											{selectedSalon?.address}
											{(selectedSalon?.city || selectedSalon?.state) && (
												<span>
													, {selectedSalon?.city}
													{selectedSalon?.state && `, ${selectedSalon.state}`}
												</span>
											)}
										</div>
										<a
											href={`https://maps.google.com/?q=${encodeURIComponent(
												`${selectedSalon?.address} ${selectedSalon?.city} ${selectedSalon?.state}`,
											)}`}
											target="_blank"
											rel="noopener noreferrer"
											className="text-sm text-blue-600 hover:text-blue-700"
										>
											Get directions
										</a>
									</div>
								</div>

								{/* Opening Times and Additional Info */}
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{/* Opening Times */}
									{selectedSalon?.salonHours && (
										<div className="bg-white rounded-lg p-6">
											<h3 className="text-lg font-semibold text-grey-900 mb-4 font-heading">
												Opening times
											</h3>
											<div className="space-y-3">
												{groupWorkingHoursByDay(selectedSalon.salonHours).map(
													(day, index) => {
														const dayName = day.day.toLowerCase();
														const today = new Date()
															.toLocaleDateString('en-US', { weekday: 'long' })
															.toLowerCase();
														const isToday = dayName === today;
														return (
															<div
																key={index}
																className="flex items-center justify-between"
															>
																<div className="flex items-center gap-3">
																	<div
																		className={`w-2 h-2 rounded-full ${
																			day.times.length > 0
																				? 'bg-green-500'
																				: 'bg-grey-300'
																		}`}
																	/>
																	<span
																		className={`capitalize ${
																			isToday
																				? 'font-medium text-grey-900'
																				: 'text-grey-700'
																		}`}
																	>
																		{day.day}
																	</span>
																</div>
																<div className="text-right">
																	{day.times.length > 0 ? (
																		<div className="space-y-1">
																			{day.times.map((slot, slotIndex) => (
																				<div
																					key={slotIndex}
																					className="text-grey-700 text-sm"
																				>
																					{formatTime(slot.openTime)} -{' '}
																					{formatTime(slot.closeTime)}
																				</div>
																			))}
																		</div>
																	) : (
																		<span className="text-grey-500">
																			Closed
																		</span>
																	)}
																</div>
															</div>
														);
													},
												)}
											</div>
										</div>
									)}

									{/* Additional Information */}
									<div className="bg-white rounded-lg p-6">
										<h3 className="text-lg font-semibold text-grey-900 mb-4 font-heading">
											Additional information
										</h3>
										<div className="space-y-3">
											<div className="flex items-start gap-3">
												<svg
													className="w-5 h-5 text-grey-400 mt-0.5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M5 13l4 4L19 7"
													/>
												</svg>
												<span className="text-grey-700">
													Instant Confirmation
												</span>
											</div>

											{/* Specialties if they exist */}
											{businessInfo?.businessSpecialties &&
												businessInfo.businessSpecialties.length > 0 && (
													<>
														<div className="pt-2 border-t border-grey-100">
															<p className="text-sm font-medium text-grey-900 mb-2">
																Specialties
															</p>
															<div className="flex flex-wrap gap-2">
																{businessInfo.businessSpecialties.map(
																	(specialty, idx) => (
																		<span
																			key={idx}
																			className="px-2 py-1 bg-grey-50 text-grey-700 rounded text-xs"
																		>
																			{toSentenceCase(specialty)}
																		</span>
																	),
																)}
															</div>
														</div>
													</>
												)}
										</div>
									</div>
								</div>
							</section>
						</div>

						{/* Desktop Cart Sidebar */}
						<div className="hidden lg:block w-96">
							<div className="sticky top-[250px]">
								<CartSummary
									showButton={true}
									customButton={{
										text:
											isVoucherPurchase || isGiftCardPurchase
												? 'Checkout'
												: 'Continue to booking',
										onClick: () => {
											if (isVoucherPurchase) {
												handleNextStep(BookingStep.VOUCHER_CHECKOUT);
												return;
											}
											if (isGiftCardPurchase) {
												handleNextStep(BookingStep.GIFT_CARD_CHECKOUT);
												return;
											}
											const nextStep = businessInfo?.allowServiceStaffSelection
												? BookingStep.STAFF_SELECTION
												: BookingStep.TIME_AND_DATE;
											handleNextStep(nextStep);
										},
										className: 'w-full bg-green-300 text-white',
									}}
								/>
							</div>
						</div>
					</div>
				</div>

				{/* Mobile Sticky Cart Button */}
				{cartCount > 0 && isMobile && (
					<div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-grey-200 lg:hidden">
						<Button
							onClick={() => {
								if (isVoucherPurchase) {
									handleNextStep(BookingStep.VOUCHER_CHECKOUT);
									return;
								}
								if (isGiftCardPurchase) {
									handleNextStep(BookingStep.GIFT_CARD_CHECKOUT);
									return;
								}
								const nextStep = businessInfo?.allowServiceStaffSelection
									? BookingStep.STAFF_SELECTION
									: BookingStep.TIME_AND_DATE;
								handleNextStep(nextStep);
							}}
							className="w-full bg-green-300   text-white h-12"
						>
							{isVoucherPurchase || isGiftCardPurchase
								? 'Checkout'
								: 'Continue'}{' '}
							• {cartCount} {cartCount === 1 ? 'item' : 'items'}
						</Button>
					</div>
				)}

				{/* Voucher Redemption Modal */}
				<VoucherRedemptionModal />

				{/* Cart Dialog */}
				<CartDialog open={isCartOpen} onOpenChange={setIsCartOpen} />
			</div>
		</>
	);
}

function NavigationTab({
	activeSection,
	label,
	onClick,
}: {
	activeSection: string;
	label: string;
	onClick: () => void;
}) {
	return (
		<button
			onClick={onClick}
			className={`cursor-pointer px-4 py-2 rounded-full whitespace-nowrap transition-all text-sm font-medium ${
				activeSection === label.toLowerCase()
					? 'bg-green-300 text-white'
					: 'bg-grey-10 text-black  '
			}`}
		>
			{label}
		</button>
	);
}
